import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { BASE_URL } from '../config';
import API from '../api';
import { showSuccess, showError, showConfirm } from '../utils/dialog';
import { formatAppDate, formatAppDateTime } from '../utils/appDateTime';

// ContactDetailModal
// - Fetches contact detail (preferring `accounts` shape)
// - Initializes form from fetched data
// - Diff-only saves: sends { accounts: { ...changedFields } }
// - Tabs: Profile | Clients | Audits
import PhoneInput, { isValidPhoneNumber, getCountryCallingCode } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ContactClientsTable from './ContactClientsTable';
import { FaEllipsisV } from 'react-icons/fa';
import RichAuditsPanel from './RichAuditsPanel';
import { resolveBackendUrl } from '../utils/accountAvatarUrl';

/** Map UI labels to values accepted by PUT /api/admin/accounts/:id/state */
function normalizeAccountStateForApi(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (s === "pending activation" || s === "pendingactivation") return "pending_activation";
  return s;
}

function UnsavedChangesDialog({ isOpen, onSaveAndContinue, onDiscardAndContinue, onReview }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-gray-200 p-6">
        <h4 className="text-lg font-semibold text-gray-900">You Have Unsaved Changes</h4>
        <p className="mt-2 text-sm text-gray-600">What would you like to do with your changes?</p>
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={onSaveAndContinue}
            className="px-4 py-2 rounded-lg bg-sidebar-active text-white text-sm font-semibold hover:bg-red-600"
          >
            SAVE & CONTINUE
          </button>
          <button
            type="button"
            onClick={onDiscardAndContinue}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            DISCARD CHANGES & CONTINUE
          </button>
          <button
            type="button"
            onClick={onReview}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            REVIEW CHANGES
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ContactDetailModal({ contact, clientId: clientIdProp, isOpen, onClose, onSaved, initialTab = 'profile', readOnly = false }) {
  const [contactDetail, setContactDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [clientNames, setClientNames] = useState({});
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const statusMenuRef = useRef(null);
  const [activationEmailSentDate, setActivationEmailSentDate] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [unsavedPromptOpen, setUnsavedPromptOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    function handleClickOutside(event) {
      if (statusMenuRef.current && !statusMenuRef.current.contains(event.target)) {
        setStatusMenuOpen(false);
      }
    }
    if (statusMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusMenuOpen]);

  const getAuthorAccountId = () => {
    try {
      const explicit = localStorage.getItem('accountId');
      if (explicit && !Number.isNaN(parseInt(explicit, 10))) return parseInt(explicit, 10);
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const u = JSON.parse(rawUser);
        return u?.account_id || u?.accountId || u?.id;
      }
    } catch (_) { }
    return undefined;
  };

  const resolvedClientId =
    clientIdProp ||
    contact?.clientId ||
    contact?.client_id ||
    contact?.client?.id ||
    contact?.client?.client_id ||
    null;

  const fetchContactDetail = async (id) => {
    if (!id) return;
    setLoadingDetail(true);
    try {
      console.debug('[ContactDetailModal] fetching contact detail for id:', id);
      // try clients-specific endpoint first, then fall back to personnel/account endpoint
      let res;
      try {
        res = await axios.get(`${BASE_URL}/api/clients/contacts/${id}`);
      } catch (e) {
        console.debug('[ContactDetailModal] clients endpoint failed, trying personnel endpoint', e?.response?.status);
        res = await axios.get(`${BASE_URL}/api/personnel/accounts/${id}`);
      }
      console.debug('[ContactDetailModal] fetch response:', res && res.data ? res.data : res);
      const data = res.data?.data || res.data || {};
      if (!data.id && data.account_id) data.id = data.account_id;
      if (!data.id && data.contact_id) data.id = data.contact_id;
      console.debug('[ContactDetailModal] resolved contact detail:', data);
      setContactDetail(data);
      // prefer accounts object when present
      const acct = data.account || data.accounts || data || {};
      const init = {
        firstName: acct.firstName || acct.first_name || '',
        lastName: acct.lastName || acct.last_name || '',
        title: acct.title || acct.job_title || '',
        email: acct.email || '',
        // keep phone as a single international value (includes country code)
        phoneNumber: acct.phoneNumber || acct.phone || acct.phone_number || '',
        extendedDurationPasscode: acct.extendedDurationPasscode || acct.extended_duration_passcode || '',
        profilePicture: acct.profilePicture || acct.profile_picture || acct.avatar || acct.avatar_url || acct.picture || '',
        createDate:
          acct.createDate ||
          acct.create_date ||
          acct.created_at ||
          acct.createdAt ||
          data.createDate ||
          data.create_date ||
          data.created_at ||
          data.createdAt ||
          data.object_create_date ||
          data.object_modify_date ||
          null,
        lastSignInDate:
          acct.lastSignInDate ||
          acct.last_sign_in ||
          acct.lastSignIn ||
          acct.signin_date ||
          null,
        state: acct.state || acct.status || 'active',
        name: acct.name || `${acct.firstName || acct.first_name || ''} ${acct.lastName || acct.last_name || ''}`.trim(),
      };

      // deep-clone initial values to avoid shared references
      const clone = JSON.parse(JSON.stringify(init));
      setForm(clone);
      setOriginal(clone);
    } catch (err) {
      console.error('Failed to load contact detail', err);
      setContactDetail(null);
      setForm({});
      setOriginal(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    // allow `contact` to be either an object { id/contact_id } or a raw id value
    const id = contact?.id || contact?.contact_id || (typeof contact === 'number' || typeof contact === 'string' ? contact : undefined);
    if (isOpen && id) fetchContactDetail(id);
    if (!isOpen) {
      setContactDetail(null);
      setForm({});
      setOriginal(null);
    }
    if (isOpen) setActiveTab(initialTab || 'profile');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, contact?.id, contact?.contact_id, initialTab]);

  // When viewing Clients tab, ensure we have client names for objectIds referenced in aclEntries
  useEffect(() => {
    if (activeTab !== 'clients') return;
    const entries = contactDetail?.aclEntries || [];
    // collect all variants of client/object id fields that may appear on acl entries
    const ids = Array.from(new Set(entries.map(a => a.objectId || a.object_id || a.clientId || a.client_id || a.object || a.object_id).filter(Boolean)));
    const toFetch = ids.filter((id) => id && !clientNames[String(id)] && Number.isFinite(Number(id)));
    if (!toFetch.length) return;

    (async () => {
      try {
        const results = await Promise.all(toFetch.map(async (id) => {
          try {
            const res = await axios.get(`${BASE_URL}/api/clients/${id}/details`);
            const name = res.data?.data?.name || res.data?.name || '';
            return { id, name };
          } catch (e) {
            return { id, name: '' };
          }
        }));
        const map = {};
        results.forEach(r => { if (r && r.id != null) map[String(r.id)] = r.name; });
        setClientNames((prev) => ({ ...prev, ...map }));
      } catch (e) {
        // don't block UI on failures
        console.debug('[ContactDetailModal] failed to fetch client names', e?.message || e);
      }
    })();
  }, [activeTab, contactDetail?.aclEntries, clientNames]);

  const handleChange = (field, value) => {
    setForm((s) => ({ ...s, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const hasUnsavedChanges = () => {
    if (!original || !form) return false;
    try {
      return JSON.stringify(form) !== JSON.stringify(original);
    } catch (_) {
      return false;
    }
  };

  const requestClose = () => {
    if (hasUnsavedChanges()) {
      setUnsavedPromptOpen(true);
      return;
    }
    onClose?.();
  };

  const handlePasswordReset = async () => {
    setResetModalOpen(true);
  };

  const handleSave = async (overrideChanges = null) => {
    const id = contact?.id || contact?.contact_id || contactDetail?.id || contactDetail?.account_id;
    if (!id) return showError('No contact selected');

    const nextErrors = {};
    if (!String(form.firstName || '').trim()) nextErrors.firstName = 'First Name is required';
    if (!String(form.lastName || '').trim()) nextErrors.lastName = 'Last Name is required';
    if (!String(form.email || '').trim()) {
      nextErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(String(form.email).trim())) {
        nextErrors.email = 'Please enter a valid email address';
      }
    }
    if (!String(form.phoneNumber || '').trim()) {
      nextErrors.phoneNumber = 'Phone Number is required';
    } else if (!isValidPhoneNumber(String(form.phoneNumber))) {
      nextErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      showError('Please fill the required fields before saving');
      return;
    }

    setFormErrors({});

    const changed = overrideChanges || {};
    if (!overrideChanges) {
      const isDifferent = (a, b) => {
        if (a == null && b == null) return false;
        if (typeof a === 'object' || typeof b === 'object') {
          try {
            return JSON.stringify(a || null) !== JSON.stringify(b || null);
          } catch (_) {
            return String(a) !== String(b);
          }
        }
        return String(a ?? '') !== String(b ?? '');
      };

      Object.keys(form).forEach((k) => {
        const origVal = original?.[k];
        const newVal = form[k];
        if (isDifferent(origVal, newVal)) changed[k] = newVal === '' ? null : newVal;
      });
    }

    if (Object.keys(changed).length === 0) {
      if (!overrideChanges) onClose?.();
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const authorId = getAuthorAccountId();
      if (authorId) headers['x-author-account-id'] = `${authorId}`;

      const accountsPayload = {};
      const fields = ['profilePicture', 'firstName', 'lastName', 'title', 'email', 'phoneNumber', 'extendedDurationPasscode', 'state'];
      fields.forEach(f => { if (f in changed) accountsPayload[f] = changed[f]; });

      if ('firstName' in changed || 'lastName' in changed) {
        const f = ('firstName' in changed) ? changed.firstName : form.firstName;
        const l = ('lastName' in changed) ? changed.lastName : form.lastName;
        accountsPayload.name = `${(f || '').trim()} ${(l || '').trim()}`.trim() || null;
      }

      const putUrls = [
        ...(resolvedClientId ? [`${BASE_URL}/api/clients/${resolvedClientId}/client-contacts/${id}`] : []),
        `${BASE_URL}/api/personnel/accounts/${id}`,
      ];
      const fieldMap = {
        firstName: 'first_name',
        lastName: 'last_name',
        preferredName: 'preferred_name',
        middleName: 'middle_name',
        email: 'email',
        phoneNumber: 'phone',
        personalNumber: 'personal_number',
        personalEmail: 'personal_email',
        emergencyName: 'emergency_name',
        emergencyPhone: 'emergency_phone',
        state: 'state',
        title: 'title',
        extendedDurationPasscode: 'extended_duration_passcode',
        profilePicture: 'profile_picture',
        name: 'name'
      };
      const toPersonnelPayload = (obj) => {
        const out = {};
        Object.keys(obj).forEach((k) => {
          const v = obj[k];
          if (v === undefined) return;
          const mapped = fieldMap[k] || k.replace(/([A-Z])/g, '_$1').toLowerCase();
          out[mapped] = v;
        });
        return out;
      };

      let res;
      for (const u of putUrls) {
        try {
          let body;
          if (u.includes('/api/personnel')) {
            body = toPersonnelPayload(accountsPayload);
          } else if (u.includes('/api/clients/') && u.includes('/client-contacts/')) {
            // client-contacts PUT expects full profile; send form values (not diff-only)
            body = toPersonnelPayload({
              firstName: form.firstName,
              lastName: form.lastName,
              title: form.title ?? "",
              email: form.email,
              phoneNumber: form.phoneNumber,
              extendedDurationPasscode: form.extendedDurationPasscode,
            });
          } else {
            body = { accounts: accountsPayload };
          }
          res = await axios.put(u, body, { headers });
          console.debug('[ContactDetailModal] PUT to', u, 'payload:', body, 'response:', res && res.data ? res.data : res);
          break;
        } catch (e) {
          if (e.response && e.response.status === 404) continue;
          throw e;
        }
      }

      if (res && (res.status === 200 || res.status === 204)) {
        showSuccess('Contact saved');
        await fetchContactDetail(id);
        onSaved?.();
      } else {
        showError('Failed to save contact');
      }
    } catch (err) {
      console.error('Contact save failed', err);
      showError(err?.response?.data?.message || err.message || 'Server error');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newState) => {
    const id = contact?.id || contact?.contact_id || contactDetail?.id || contactDetail?.account_id;
    if (!id) return;
    const apiState = normalizeAccountStateForApi(newState);
    const confirmed = await showConfirm(
      `Are you sure you want to set this account to ${newState}?`,
      "Confirm"
    );
    if (!confirmed) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const authorId = getAuthorAccountId();
      if (authorId) headers['x-author-account-id'] = `${authorId}`;

      const attempts = [];
      if (resolvedClientId && apiState === "active") {
        attempts.push({
          url: `${BASE_URL}/api/clients/${resolvedClientId}/client-contacts/${id}/state`,
          body: { state: "active" },
        });
      }
      attempts.push({
        url: `${BASE_URL}/api/admin/accounts/${id}/state`,
        body: { state: apiState },
      });

      let updated = false;
      let lastError = null;
      for (const { url, body } of attempts) {
        try {
          const res = await axios.put(url, body, { headers });
          const success =
            res.status >= 200 &&
            res.status < 300 &&
            res.data?.success !== false;
          if (success) {
            updated = true;
            break;
          }
        } catch (e) {
          lastError = e;
          if (e.response?.status === 404) continue;
          throw e;
        }
      }

      if (!updated) {
        throw lastError || new Error("Failed to update account status");
      }

      showSuccess(`Account status updated to ${newState}`);
      await fetchContactDetail(id);
      onSaved?.();
    } catch (err) {
      console.error('Status update failed', err);
      showError(err?.response?.data?.message || err.message || 'Server error');
    } finally {
      setSaving(false);
      setStatusMenuOpen(false);
    }
  };

  const handleSendActivationEmail = async () => {
    const id = contact?.id || contact?.contact_id || contactDetail?.id || contactDetail?.account_id;
    if (!id) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const authorId = getAuthorAccountId();
      if (authorId) headers['x-author-account-id'] = `${authorId}`;

      const tryUrls = [
        `${BASE_URL}/api/personnel/${id}/send-activation-email`,
        `${BASE_URL}/api/personnel/accounts/${id}/send-activation-email`,
        `${BASE_URL}/api/personnel/accounts/${id}/send-activation`,
        `${BASE_URL}/api/clients/contacts/${id}/send-activation`,
      ];
      const tempPassword = `Tmp${Math.random().toString(36).slice(-8)}!`;
      let ok = false;
      for (const url of tryUrls) {
        try {
          await API.post(url, { password: tempPassword }, { headers });
          ok = true;
          break;
        } catch (e) {
          if (e.response && e.response.status === 404) continue;
          throw e;
        }
      }
      if (!ok) throw new Error('Activation endpoint not found');
      setActivationEmailSentDate(new Date().toISOString());
      showSuccess('Activation email sent');
    } catch (err) {
      console.error('Failed to send activation email', err);
      showError(err?.response?.data?.message || err.message || 'Server error');
    } finally {
      setSaving(false);
      setStatusMenuOpen(false);
    }
  };

  const handleProfileUpload = (e) => {
    // Kept for backwards compatibility; prefer uploadAvatar which mirrors Personnel behavior
    const f = e?.target?.files?.[0];
    if (!f) return;
    uploadAvatar(e);
  };

  const fileInputRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const uploadAvatar = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const id = contact?.id || contact?.contact_id || contactDetail?.id || contactDetail?.account_id;
    if (!id) return showError('No contact selected');

    // show a quick preview while uploading
    try {
      const previewUrl = URL.createObjectURL(file);
      setForm((s) => ({ ...s, profilePicture: previewUrl }));
    } catch (_) { }

    setUploadingAvatar(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const authorId = getAuthorAccountId();
      if (authorId) headers['x-author-account-id'] = `${authorId}`;

      const formData = new FormData();
      formData.append('file', file);

      // try clients-specific avatar endpoint first, then personnel endpoint
      const avatarUrls = [
        `${BASE_URL}/api/clients/contacts/${id}/avatar`,
        `${BASE_URL}/api/personnel/accounts/${id}/avatar`,
      ];
      let uploaded = false;
      for (const u of avatarUrls) {
        try {
          await axios.post(u, formData, { headers });
          uploaded = true;
          break;
        } catch (err) {
          if (err.response && err.response.status === 404) continue;
          throw err;
        }
      }
      if (!uploaded) throw new Error('Avatar endpoint not found');
      showSuccess('Avatar uploaded');
      // Refresh detail from server to get canonical avatar URL
      await fetchContactDetail(id);
    } catch (err) {
      console.error('Avatar upload failed', err);
      showError(err?.response?.data?.message || err.message || 'Avatar upload failed');
    } finally {
      setUploadingAvatar(false);
      try { if (fileInputRef.current) fileInputRef.current.value = ''; } catch (_) { }
    }
  };

  // Helper: format audit timestamp into readable string
  function formatAuditDateTime(dateTime) {
    return formatAppDateTime(dateTime, { emptyValue: '—' });
  }

  function formatAuditRow(audit) {
    if (!audit) return { Date: '—', Action: '—', Property: '—', Value: '—', PerformedBy: 'Unknown' };
    if (audit.action !== undefined || audit.audit_id !== undefined) {
      const action = audit.action || audit.name_label || '—';
      const property = audit.property || '—';
      const value = audit.value !== undefined && audit.value !== null
        ? String(audit.value)
        : (audit.data ? (typeof audit.data === 'string' ? audit.data : JSON.stringify(audit.data)) : '—');
      const performedBy = audit.performed_by || `${audit.first_name || ''} ${audit.last_name || ''}`.trim() || 'Unknown';
      return { Date: formatAuditDateTime(audit.date || audit.created_at || audit.timestamp), Action: action, Property: property, Value: value, PerformedBy: performedBy };
    }
    // fallback shape
    const primary = (audit.metas && audit.metas.length > 0) ? audit.metas[0] : null;
    const action = primary ? (primary.action || primary.name || '—') : (audit.type || '—');
    const property = primary ? (primary.property || '—') : '—';
    let value = '—';
    if (primary) {
      if (primary.value !== undefined && primary.value !== null) value = String(primary.value);
      else if (primary.data !== undefined && primary.data !== null) value = typeof primary.data === 'string' ? primary.data : JSON.stringify(primary.data);
    }
    const performedBy = `${audit.first_name || ''} ${audit.last_name || ''}`.trim() || audit.performed_by || 'Unknown';
    return { Date: formatAuditDateTime(audit.date || audit.created_at || audit.timestamp), Action: action, Property: property, Value: value, PerformedBy: performedBy };
  }

  function formatDisplayDate(date) {
    return formatAppDateTime(date, { emptyValue: '—' });
  }

  function Field({ label, value, hint }) {
    return (
      <div className="flex flex-col gap-1">
        <div className="text-xs font-medium text-gray-600">{label}</div>
        <div className="border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px] overflow-x-auto whitespace-nowrap">
          {value ?? ""}
        </div>
        {hint ? <div className="text-xs text-gray-400">{hint}</div> : null}
      </div>
    );
  }

  function Section({ title, children }) {
    return (
      <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-white">
        <div className="text-sm font-semibold text-gray-700">{title}</div>
        {children}
      </div>
    );
  }

  function AuditsPanel({ id }) {
    return (
      <RichAuditsPanel
        subjectId={id}
        fetchUrl={id ? `${BASE_URL}/api/personnel/accounts/${id}/audits/rich` : ""}
        title="Audit History"
      />
    );
  }
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onMouseDown={(e) => { if (e.target === e.currentTarget) requestClose(); }}>
      <div className="bg-white w-full max-w-5xl h-[80vh] rounded-lg shadow-lg overflow-hidden flex flex-col">
        <div className="flex items-start justify-between p-4 border-b border-gray-200">
          <div className="text-sm">
            <div className="font-semibold text-gray-700">Contact #{contactDetail?.id ?? contactDetail?.account_id ?? contact?.id ?? contact?.contact_id ?? '—'}</div>
            <div className="text-xl text-gray-900">{form?.firstName || form?.name || 'Contact'}</div>
          </div>
          <button onClick={requestClose} className="text-gray-500 hover:text-gray-700">✖</button>
        </div>

        <div className="flex flex-col sm:flex-row flex-1 min-h-0 overflow-hidden">
          <aside className="w-full sm:w-56 border-b sm:border-b-0 sm:border-r border-gray-200 p-3 overflow-x-auto sm:overflow-y-auto bg-gray-50 flex-shrink-0">
            <nav className="flex flex-row sm:flex-col gap-1 whitespace-nowrap">
              {[{ id: 'profile', label: 'Profile' }, { id: 'clients', label: 'Clients' }, { id: 'audits', label: 'Audits' }].map((t) => (
                <button
                  key={t.id}
                  className={`flex-shrink-0 sm:w-full text-left px-3 py-2 rounded transition-colors ${activeTab === t.id ? 'bg-sidebar-active text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </aside>

          <section className="flex-1 min-w-0 overflow-y-auto p-4 bg-white">
            <fieldset disabled={readOnly} className="border-0 m-0 p-0 min-h-0 min-w-0 h-full">
            {activeTab === 'profile' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main content (left two columns) */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Profile Information</h3>

                    {loadingDetail ? (
                      <div className="text-center text-gray-500">Loading contact…</div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2 flex items-start gap-6">
                          <div className="relative w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                            {form.profilePicture ? (
                              <img src={resolveBackendUrl(form.profilePicture)} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                              <div className="text-gray-400">No Image</div>
                            )}
                            {/* hidden file input; triggered by camera overlay */}
                            <input ref={fileInputRef} id="contact-avatar-input" type="file" accept="image/*" onChange={uploadAvatar} style={{ display: 'none' }} />

                            <label
                              htmlFor="contact-avatar-input"
                              title="Upload avatar"
                              className="absolute bottom-2 right-2 bg-white rounded-full p-2 shadow-md border border-gray-200 cursor-pointer z-10"
                            >
                              {uploadingAvatar ? (
                                <svg className="animate-spin h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v4a1 1 0 001 1h3m10 0h3a1 1 0 001-1V7M7 10l3-3m0 0l3 3M10 7v8" /></svg>
                              )}
                            </label>
                          </div>

                          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                            <div>
                              <label className="block text-sm font-medium">First Name *</label>
                              <input type="text" value={form.firstName || ''} onChange={(e) => handleChange('firstName', e.target.value)} className={`w-full border rounded-xl px-4 py-3 ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`} />
                              {formErrors.firstName && <div className="text-red-600 text-xs mt-1">{formErrors.firstName}</div>}
                            </div>

                            <div>
                              <label className="block text-sm font-medium">Last Name *</label>
                              <input type="text" value={form.lastName || ''} onChange={(e) => handleChange('lastName', e.target.value)} className={`w-full border rounded-xl px-4 py-3 ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`} />
                              {formErrors.lastName && <div className="text-red-600 text-xs mt-1">{formErrors.lastName}</div>}
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium">Title</label>
                          <input type="text" value={form.title || ''} onChange={(e) => handleChange('title', e.target.value)} className="w-full border border-gray-300 rounded-xl px-4 py-3" />
                        </div>

                        <div>
                          <label className="block text-sm font-medium">Email *</label>
                          <input
                            type="email"
                            value={form.email || ''}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className={`w-full border rounded-xl px-4 py-3 ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                          />
                          {formErrors.email && <div className="text-red-600 text-xs mt-1">{formErrors.email}</div>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium">Phone Number *</label>
                          <div className={`w-full border rounded-xl px-4 py-3 ${formErrors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}>
                            <PhoneInput
                              value={form.phoneNumber || ''}
                              onChange={(value) => handleChange('phoneNumber', value)}
                              onCountryChange={(country) => {
                                if (!country) return;
                                const code = `+${getCountryCallingCode(country)}`;
                                setForm((prev) => {
                                  const current = prev.phoneNumber || '';
                                  if (String(current).startsWith(code)) return prev;
                                  return { ...prev, phoneNumber: code };
                                });
                              }}
                              international
                              withCountryCallingCode
                              countryCallingCodeEditable={false}
                              defaultCountry="CA"
                              className="w-full PhoneInput"
                            />
                          </div>
                          {formErrors.phoneNumber && <div className="text-red-600 text-xs mt-1">{formErrors.phoneNumber}</div>}
                        </div>

                        <div>
                          <label className="block text-sm font-medium">Passcode (Extended Duration Passcode)</label>
                          <input type="text" value={form.extendedDurationPasscode || ''} onChange={(e) => handleChange('extendedDurationPasscode', e.target.value)} className="w-full border border-gray-300 rounded-xl px-4 py-3" />
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                      <button onClick={() => handleSave()} disabled={saving} className="px-6 py-3 bg-sidebar-active text-white rounded-xl disabled:opacity-60">Save Changes</button>
                    </div>
                  </div>
                </div>

                {/* Right sidebar (personnel-style) */}
                <div className="space-y-6 lg:sticky lg:top-0 self-start">
                  <Section title="Account Status">
                    <div className="grid grid-cols-1 gap-3">
                      <Field label="Created" value={formatDisplayDate(form.createDate)} />

                      <div className="flex flex-col gap-1">
                        <div className="text-xs font-medium text-gray-600">Account Status</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-gray-900 min-h-[38px]">
                            {(() => {
                              const s = (form.state || "").toLowerCase();
                              if (s === "pending" || s === "pendingactivation" || s === "pending_activation" || s === "pending activation") return "Pending Activation";
                              if (s === "active") return "Active";
                              if (s === "deleted") return "Deleted";
                              return form.state || '';
                            })()}
                          </div>
                          <div className="relative" ref={statusMenuRef}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setStatusMenuOpen(!statusMenuOpen);
                              }}
                              className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                              title="Account Status Options"
                            >
                              <FaEllipsisV className="text-gray-600" />
                            </button>
                            {statusMenuOpen && (
                              <>
                                <div
                                  className="fixed inset-0 z-40"
                                  onClick={() => setStatusMenuOpen(false)}
                                />
                                <div className="absolute right-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                  {(form.state || "").toLowerCase() === "active" ? (
                                    <div
                                      className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                      onClick={() => handleStatusChange("deleted")}
                                    >
                                      <div className="text-sm font-medium text-red-600">Delete Account</div>
                                      <div className="text-xs text-gray-500 mt-1">
                                        Remove the account from the system, the person will not able to Sign-in
                                      </div>
                                    </div>
                                  ) : (form.state || "").toLowerCase() === "deleted" ? (
                                    <>
                                      <div
                                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                        onClick={() => handleStatusChange("active")}
                                      >
                                        <div className="text-sm font-medium text-green-600">Active</div>
                                      </div>
                                      <div
                                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleStatusChange("pending activation")}
                                      >
                                        <div className="text-sm font-medium text-blue-600">Pending Activation</div>
                                      </div>
                                    </>
                                  ) : (form.state || "").toLowerCase().includes("pending") ? (
                                    <>
                                      <div
                                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                        onClick={async () => {
                                          const confirmed = await showConfirm(
                                            activationEmailSentDate
                                              ? 'Are you sure you want to resend the activation email?'
                                              : 'Are you sure you want to send the activation email?',
                                            'Confirm'
                                          );
                                          if (confirmed) await handleSendActivationEmail();
                                        }}
                                      >
                                        <div className="text-sm font-medium text-blue-600">
                                          {activationEmailSentDate ? "Resend Activation Email" : "Send Activation Email"}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          An email will be sent to {form.email} to setup their sign-in password
                                        </div>
                                      </div>
                                      <div
                                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                                        onClick={() => handleStatusChange("active")}
                                      >
                                        <div className="text-sm font-medium text-green-600">Activate Account</div>
                                        <div className="text-xs text-gray-500 mt-1">
                                          Account will be activated without notifying the person
                                        </div>
                                      </div>
                                      <div
                                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleStatusChange("deleted")}
                                      >
                                        <div className="text-sm font-medium text-red-600">Delete Account</div>
                                      </div>
                                    </>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        {(form.state || "").toLowerCase().includes("pending") && (
                          <div className="text-xs text-gray-500 mt-1">
                            {activationEmailSentDate
                              ? `Activation email sent ${formatAppDate(activationEmailSentDate, { emptyValue: '' })}`
                              : "Notification Not Sent"}
                          </div>
                        )}
                      </div>
                    </div>
                  </Section>

                  <Section title="Sign-in & Security">
                    <div className="grid grid-cols-1 gap-3">
                      <Field label="Email" value={form.email} />

                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-3">
                          <Field label="Password" value="••••••••" />
                        </div>
                        <button
                          className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          onClick={handlePasswordReset}
                        >
                          Reset Password
                        </button>
                      </div>

                      <Field label="Last Sign-In" value={form.lastSignInDate ? formatDisplayDate(form.lastSignInDate) : 'Never'} />
                    </div>
                  </Section>
                </div>
              </div>
            )}

            {activeTab === 'clients' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <ContactClientsTable accountId={contactDetail?.id || contactDetail?.account_id || contact?.id || contact?.contact_id} />
              </div>
            )}

            {activeTab === 'audits' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <AuditsPanel id={contactDetail?.id || contactDetail?.account_id || contact?.id || contact?.contact_id} />
              </div>
            )}

            {activeTab === 'security' && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xl font-semibold mb-4">Sign-in & Security</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium">Email</label>
                    <input type="email" value={form.email || ''} onChange={(e) => handleChange('email', e.target.value)} className="w-full border border-gray-300 rounded-xl px-4 py-3" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium">Password</label>
                    <div className="flex items-center gap-2">
                      <button onClick={handlePasswordReset} className="px-4 py-2 bg-red-600 text-white rounded">Reset Password</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </fieldset>
          </section>
        </div>
      </div>
      <UnsavedChangesDialog
        isOpen={unsavedPromptOpen}
        onSaveAndContinue={async () => {
          setUnsavedPromptOpen(false);
          await handleSave();
        }}
        onDiscardAndContinue={() => {
          setUnsavedPromptOpen(false);
          setForm(JSON.parse(JSON.stringify(original || {})));
          onClose?.();
        }}
        onReview={() => setUnsavedPromptOpen(false)}
      />
      {resetModalOpen && (
        <ResetPasswordModal
          person={contactDetail}
          email={form.email}
          isOpen={resetModalOpen}
          onClose={() => setResetModalOpen(false)}
        />
      )}
    </div>
  );
}

function ResetPasswordModal({ person, isOpen, onClose, email }) {
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [generated, setGenerated] = useState("");
  const [custom, setCustom] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [forceChange, setForceChange] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setSendEmail(true);
      setAutoGenerate(true);
      setForceChange(true);
      const pwd = generatePassword(12);
      setGenerated(pwd);
      setCustom("");
      setShowPassword(false);
      setError("");
    }
  }, [isOpen, person?.id]);

  useEffect(() => {
    if (isOpen && autoGenerate) {
      setGenerated(generatePassword(12));
    }
  }, [autoGenerate, isOpen]);

  function generatePassword(len = 12) {
    const lower = "abcdefghijklmnopqrstuvwxyz";
    const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const syms = "!@#$%^&*()-_=+[]{}<>?";
    const all = lower + upper + nums + syms;
    let out = "";
    out += lower[Math.floor(Math.random() * lower.length)];
    out += nums[Math.floor(Math.random() * nums.length)];
    out += upper[Math.floor(Math.random() * upper.length)];
    out += syms[Math.floor(Math.random() * syms.length)];
    for (let i = out.length; i < len; i++)
      out += all[Math.floor(Math.random() * all.length)];
    return out
      .split("")
      .sort(() => Math.random() - 0.5)
      .join("");
  }

  function validatePassword(p) {
    const errs = [];
    if (!p || p.length < 8) errs.push("At least 8 characters");
    if (!/[a-z]/.test(p)) errs.push("One lowercase letter");
    if (!/[A-Z]/.test(p)) errs.push("One uppercase letter");
    if (!/[0-9]/.test(p)) errs.push("One number");
    if (!/[!@#$%^&*()\-_=+\[\]{}<>?]/.test(p))
      errs.push("One special character");
    return errs;
  }

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError("");
    const password = autoGenerate ? generated : custom;
    if (!password) {
      setError("Password is required");
      setSaving(false);
      return;
    }
    if (!autoGenerate) {
      const errs = validatePassword(password);
      if (errs.length) {
        setError(errs.join(", "));
        setSaving(false);
        return;
      }
    }

    try {
      const body = {
        password,
        email: email || null,
        send_email: !!sendEmail,
        force_change_on_next_signin: !!forceChange,
        auto_generated: !!autoGenerate,
      };

      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      let res;
      const accountId = person?.id || person?.account_id;
      const tryUrls = [
        `${BASE_URL}/api/clients/contacts/${accountId}/reset-password`,
        `${BASE_URL}/api/personnel/accounts/${accountId}/reset-password`,
        `${BASE_URL}/api/personnel/${accountId}/reset-password`,
      ];

      let ok = false;
      for (const url of tryUrls) {
        try {
          res = await axios.put(url, body, { headers });
          ok = true;
          break;
        } catch (e) {
          if (e.response && e.response.status === 404) continue;
          throw e;
        }
      }

      if (!ok) throw new Error("Reset endpoint not found");

      showSuccess("Password reset successfully");
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to reset password");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-semibold">Reset Password</div>
            <div className="text-sm text-gray-500 mt-1">
              Set a new password and optionally email it to the contact.
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 font-bold"
          >
            ✖
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="inline-flex items-center gap-2">
            <input
              style={{ accentColor: "#dc2626" }}
              type="checkbox"
              checked={autoGenerate}
              onChange={(e) => setAutoGenerate(e.target.checked)}
            />
            <span className="text-sm">Automatically generate new password</span>
          </label>

          {autoGenerate ? (
            <div className="space-y-2">
              <div className="text-xs text-gray-500 mb-1">
                Generated Password
              </div>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={generated}
                  type={showPassword ? "text" : "password"}
                  className="flex-1 border border-gray-200 rounded px-3 py-2 bg-gray-50"
                />
                <button
                  onClick={() => setShowPassword((s) => !s)}
                  className="p-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                  title={showPassword ? "Hide" : "Show"}
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-7.5a10.94 10.94 0 0 1 1.64-3.02" />
                      <path d="M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
                <button
                  onClick={() => copyToClipboard(generated)}
                  className="p-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                  title="Copy"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="2" width="6" height="4" rx="1" />
                    <path d="M9 8H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2">
                <input
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  placeholder="Customized Password"
                  className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white"
                />
                <button
                  onClick={() => setShowPassword((s) => !s)}
                  className="p-2 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                >
                  {showPassword ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9.27-3.11-11-7.5a10.94 10.94 0 0 1 1.64-3.02" />
                      <path d="M1 1l22 22" />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                style={{ accentColor: "#dc2626" }}
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
              />
              <span className="text-sm">Email new password to contact</span>
            </label>

            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                style={{ accentColor: "#dc2626" }}
                type="checkbox"
                checked={forceChange}
                onChange={(e) => setForceChange(e.target.checked)}
              />
              <span className="text-sm">Force password change on next sign-in</span>
            </label>
          </div>

          {error && <div className="text-xs text-red-600 font-medium">{error}</div>}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleReset}
              disabled={saving}
              className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {saving ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


